// Bounded automatic-recovery budget for a single player tile.
//
// hls.js emits recoverable errors (a buffer stall, a bad segment fetch) that
// are usually worth retrying automatically — but "usually" is not "always",
// and a tile that retries forever on a genuinely dead camera just spins
// silently. This caps how many automatic recoveries happen inside a sliding
// window, with backoff between them, and reports when the budget is spent so
// the caller can surface a real error instead of retrying forever.
export type RecoveryKind = "media" | "network";

export interface RecoveryDecision {
  allow: boolean;
  delayMs: number;
  attempt: number;
}

export interface RecoveryBudgetOptions {
  maxMediaRecoveries: number;
  maxNetworkRestarts: number;
  windowMs: number;
  /** Backoff per attempt index; the last value repeats once exhausted. */
  backoffMs: number[];
}

export const DEFAULT_RECOVERY_OPTIONS: RecoveryBudgetOptions = {
  maxMediaRecoveries: 2,
  maxNetworkRestarts: 3,
  windowMs: 60_000,
  backoffMs: [0, 1_000, 3_000],
};

export interface RecoveryBudget {
  request: (kind: RecoveryKind) => RecoveryDecision;
  /** Sustained playback refills both budgets — an all-day view that hiccups
   *  every few hours must not eventually exhaust itself. */
  noteProgress: () => void;
}

export function createRecoveryBudget(
  options: RecoveryBudgetOptions = DEFAULT_RECOVERY_OPTIONS,
  now: () => number = () => Date.now(),
): RecoveryBudget {
  const history: Record<RecoveryKind, number[]> = { media: [], network: [] };

  const prune = (kind: RecoveryKind) => {
    const cutoff = now() - options.windowMs;
    history[kind] = history[kind].filter((t) => t > cutoff);
  };

  return {
    request(kind) {
      prune(kind);
      const spent = history[kind].length;
      const max = kind === "media" ? options.maxMediaRecoveries : options.maxNetworkRestarts;

      if (spent >= max) {
        return { allow: false, delayMs: 0, attempt: spent };
      }

      history[kind].push(now());
      const delayMs = options.backoffMs[Math.min(spent, options.backoffMs.length - 1)] ?? 0;
      return { allow: true, delayMs, attempt: spent + 1 };
    },
    noteProgress() {
      history.media = [];
      history.network = [];
    },
  };
}
